import React, { useState, useCallback, useEffect } from "react"
import { IoSearchOutline } from "react-icons/io5"
import { CiExport } from "react-icons/ci"
import * as XLSX from "xlsx"

const FilteredPatientsContainer = ({ filteredPatients }) => {
	const [query, setQuery] = useState("")
	// eslint-disable-next-line no-unused-vars
	const [filters, setFilters] = useState({
		filterReason: [],
		patientName: "",
		mrn: "",
		acuity: [],
		chair: [],
		startTime: "",
		endTime: "",
		lengthRange: [0, 12],
		progressRange: "",
		nurse: [],
		infusionType: [],
		reasonForVisit: [],
		referringDepartment: [],
		patientStatus: []
	})
	const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })
	const [displayData, setDisplayData] = useState([])

	const filterAndSortData = useCallback(() => {
		if (!filteredPatients || !Array.isArray(filteredPatients)) {
			setDisplayData([])
			return
		}

		let data = [...filteredPatients]

		// Filter by search query
		if (query) {
			data = data.filter(item =>
				item.patientName?.toLowerCase().includes(query.toLowerCase())
			)
		}

		// Apply filters
		if (filters.filterReason && filters.filterReason.length > 0) {
			data = data.filter(item => filters.filterReason.includes(item.filterReason))
		}

		// Sort data
		if (sortConfig.key) {
			data = data.slice().sort((a, b) => {
				let aValue = a[sortConfig.key]
				let bValue = b[sortConfig.key]

				if (aValue < bValue) {
					return sortConfig.direction === "asc" ? -1 : 1
				}
				if (aValue > bValue) {
					return sortConfig.direction === "asc" ? 1 : -1
				}
				return 0
			})
		}

		setDisplayData(data)
	}, [filteredPatients, filters, sortConfig, query])

	useEffect(() => {
		filterAndSortData()
	}, [filterAndSortData])

	const handleSearch = e => {
		setQuery(e.target.value.toLowerCase())
	}

	const handleExport = () => {
		const exportData = displayData.map(item => ({
			"Patient Name": item.patientName || "",
			MRN: item.patientMRN || "",
			"Filter Reason": item.filterReason || ""
		}))

		const worksheet = XLSX.utils.json_to_sheet(exportData)
		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Patients")
		XLSX.writeFile(workbook, "filtered_patients.xlsx")
	}

	const handleSort = key => {
		setSortConfig(prevConfig => ({
			key,
			direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc"
		}))
	}

	// Early return after all hooks
	if (!filteredPatients || !Array.isArray(filteredPatients) || filteredPatients.length === 0) {
		return null
	}

	return (
		<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 my-4 overflow-y-auto">
			<h2 className="text-2xl font-semibold text-left text-yellow-800 mb-2">
				Filtered Patients
			</h2>
			<div className="flex justify-between items-center px-4 py-4 bg-white sticky top-0 z-20">
				<div className="relative w-full max-w-xs flex items-center">
					<IoSearchOutline className="absolute left-3 text-gray-400" />
					<input
						type="text"
						className="
							sensitive ph-no-capture
							py-5 px-4 w-screen border border-transparent rounded-md pl-8 
							text-sm h-8 bg-gray-100 text-gray-600 placeholder-gray-500 
							outline-none focus:outline-none focus:ring-0 focus:border-transparent"
						placeholder="Search by Patient Name"
						value={query}
						onChange={handleSearch}
					/>
				</div>
				<div className="flex items-center space-x-2">
					<button
						className="py-2 px-4 bg-white border border-gray-300 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-100 focus:outline-none"
						onClick={handleExport}
					>
						<div className="flex items-center gap-1">
							<span className="inline-flex">
								<CiExport className="h-4 w-4 text-gray-600" />
							</span>
							<span className="font-medium">Export</span>
						</div>
					</button>
				</div>
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th
								className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
								onClick={() => handleSort("patientName")}
							>
								Patient Name
								{sortConfig.key === "patientName" && (
									<span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
								)}
							</th>
							<th
								className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
								onClick={() => handleSort("patientMRN")}
							>
								MRN
								{sortConfig.key === "patientMRN" && (
									<span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
								)}
							</th>
							<th
								className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
								onClick={() => handleSort("filterReason")}
							>
								Filter Reason
								{sortConfig.key === "filterReason" && (
									<span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
								)}
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{displayData.map((patient, index) => (
							<tr key={index} className="hover:bg-gray-50">
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
									{patient.patientName || ""}
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
									{patient.patientMRN || ""}
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
									{patient.filterReason || ""}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default FilteredPatientsContainer
